/**
 * kisiListService.js — Kişi Listesi (denetmen tarafı kişi listesi)
 *
 * Sayfa: pages/Management/Kisi/TumPersonellerPage.jsx (/Management/Kisi/Personeller)
 * Uç:    GET /api/v1/personnel/kisi-listesi — tek çağrı, fuarId verilirse o fuarda
 *        görevli herkes (tüm firmalar), verilmezse sistemdeki tüm kişiler.
 * Rehber: docs/guides/FE-PersonnelController-guide.md → "the kişi directory"
 *
 * Notlar:
 *  · Yalnızca SAKA personeli (firma hesabı 403).
 *  · `size` en fazla 200.
 *  · `belgeler=true` fuarsız da çalışır (backend #119): genel meslek kuralları, yaş kuralı yok.
 *  · `cardEligible` yalnızca fuar + belgeler ile gelir; fuarsız hesaplanmaz.
 *  · Süzgeçlerin hepsi sunucu tarafında, sayfalamadan ÖNCE çalışır (backend #120) — `total`
 *    ve her sayfa doğrudur. `suzgec` nesnesi sorgu parametrelerine 1:1 eşlenir; Excel dışa
 *    aktarma (#122) aynı nesneyi yeniden kullanabilir.
 *  · #359/#373/#361: firmaBelgeTam (+ firmaBelgeAktifFuar), kartBasim, kkd, kartSeviyesi süzgeçleri;
 *    `aktifFuarId` üst çubuk fuarıdır, Tüm Kişiler'de satırlara kartBasim/kkd taşır (bkz. suzgeciParametreye).
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";
import { KIMLIK_BELGE_TURLERI } from './kisiAdminService';

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;
const MAX_SIZE = 200;

export const USE_MOCK_DATA = false;

// ── Süzgeç modeli ────────────────────────────────────────────────────────────

/**
 * Belge başına dört durumlu süzgeç (#271/#272): '' (Hepsi) · 'var' (Onaylı, yeşil) · 'bekliyor' (sarı —
 * dosya var, Beklemede okunuyor; pratikte çoğu süresi dolmuş) · 'yok' (dosya yok, reddedilmiş ya da fuarın
 * yaş kuralına göre çok eski). Uç her anahtarı en fazla bir listeye kabul eder.
 */
export const BELGE_SUZGEC_ANAHTARLARI = [
  { key: 'kvkk', label: 'KVKK' },
  { key: 'sgk', label: 'SGK' },
  { key: 'isg', get label() { return i18n.t("services:kisiListService.isgEgitimi"); } },
  { key: 'saglik', get label() { return i18n.t("services:kisiListService.saglikOnayi"); } },
  { key: 'meslekiEgitim', get label() { return i18n.t("services:kisiListService.meslekiEgitim"); } },
];

export const FIRMA_TIPI_SECENEKLERI = [
  { value: 'katilimci', get label() { return i18n.t("services:kisiListService.katilimci"); } },
  { value: 'dekorator', get label() { return i18n.t("services:kisiListService.dekorator"); } },
  { value: 'taseron', get label() { return i18n.t("services:kisiListService.taseron"); } },
  { value: 'organizator', get label() { return i18n.t("services:kisiListService.organizator"); } },
  { value: 'belirsiz', get label() { return i18n.t("services:kisiListService.belirsiz"); } },
];

// #470: "Kimlik doğrulama tipi" — kimliğin neye bakılarak doğrulandığı (kimlik doğrulamadan AYRI süzgeç, ikisi AND'lenir).
// Değerler uçtaki `kimlikTipi` ile aynı; "belirtilmemis" = türü hiç kaydedilmemiş.
export const KIMLIK_TIPI_BELIRTILMEMIS = 'belirtilmemis';
export const KIMLIK_TIPI_SECENEKLERI = [
  ...KIMLIK_BELGE_TURLERI.map((tur) => ({ value: tur.filtre, get label() { return tur.label; } })),
  { value: KIMLIK_TIPI_BELIRTILMEMIS, get label() { return i18n.t("services:kisiListService.kimlikTipiBelirtilmemis"); } },
];

export const BOS_SUZGEC = Object.freeze({
  kimlik: '',            // dogrulandi | dogrulanmadi | alanda
  kimlikTipi: '',        // KIMLIK_TIPI_SECENEKLERI (#470) — iki görünümde de
  eksikBelge: '',        // 'true' (eksik olanlar) | 'false' (tam olanlar)
  kartDurumu: '',        // KART_ALABILIR | EVRAK_TAMAM_KART_ALAMAZ | EVRAK_EKSIK — yalnızca fuarla
  firmaTipi: '',         // FIRMA_TIPI_SECENEKLERI — yalnızca fuarla
  kartSeviyesi: '',      // TAM | YETERLI | OZEL_IZIN — yalnızca fuar görünümünde (#361/#362)
  firmaBelgeTam: '',     // 'true' (firma evrakları tam) | 'false' (eksik) — #359, iki görünümde de
  firmaBelgeAktifFuar: false, // "Aktif fuara göre": yalnızca Tüm Kişiler + firmaBelgeTam + üst çubuk fuarı (#359)
  kartBasim: '',         // basildi | basilmadi | basildi_kkd_uygun — fuar (fuarId ya da üst çubuk fuarı) gerekir (#359/#373)
  kkd: '',               // uygun | uyari | eksik — fuar gerekir; basildi_kkd_uygun ile birlikte gönderilemez (#373/#374)
  firma: null,           // { firmaId, firmaName, firmaShortname }
  belge: Object.freeze({}), // { sgk: 'var' | 'bekliyor' | 'yok', … }
});

// Seçenek listeleri: hem panel (KisiFiltreleri) hem URL okuma doğrulaması aynı yerden beslenir.
export const KART_SEVIYESI_SECENEKLERI = [
  { value: 'TAM', label: 'Tam' },
  { value: 'YETERLI', label: 'Yeterli' },
  { value: 'OZEL_IZIN', label: 'Özel İzin' },
];

export const KART_BASIM_SECENEKLERI = [
  { value: 'basildi', label: 'Basılmış' },
  { value: 'basilmadi', label: 'Basılmamış' },
  { value: 'basildi_kkd_uygun', label: 'Basılmış + KKD uygun' },
];

export const KKD_SECENEKLERI = [
  { value: 'uygun', label: 'Uygun' },
  { value: 'uyari', label: 'Uyarı' },
  { value: 'eksik', label: 'Eksik' },
];

export const KART_BASILDI_KKD_UYGUN = 'basildi_kkd_uygun';

// Belge durumu → uç parametresi. Aynı eşleme URL'de de kullanılır (#262), ad farkı olmasın diye.
const BELGE_DURUM_PARAMETRESI = { var: 'belgeVar', bekliyor: 'belgeBekliyor', yok: 'belgeYok' };
const BELGE_ANAHTARLARI = BELGE_SUZGEC_ANAHTARLARI.map((b) => b.key);

/**
 * Süzgeci uç parametrelerine çevirir. Fuar gerektiren süzgeçler fuarsız GÖNDERİLMEZ (uç 400 verir).
 *
 * `fuarId`      — Fuara Eşlenmiş görünümünün fuarı (Tüm Kişiler'de null).
 * `aktifFuarId` — üst çubuktaki fuar (#359). Fuar görünümünde fuarId ile aynıdır; uç farklı değeri 400'ler.
 *   · kartDurumu / kartSeviyesi / firmaTipi: yalnızca fuarId (kart kararı fuar+firma başınadır).
 *   · kartBasim / kkd: fuarId YA DA aktifFuarId — Tüm Kişiler'de de üst çubuk fuarında aranır (#359/#373).
 *   · firmaBelgeAktifFuar: yalnızca Tüm Kişiler (fuarId yok) + firmaBelgeTam + aktifFuarId (yoksa/çakışırsa 400).
 *   · kartBasim=basildi_kkd_uygun ile kkd birlikte gönderilemez (400): kkd düşer.
 */
export function suzgeciParametreye(suzgec = BOS_SUZGEC, { fuarId = null, aktifFuarId = null } = {}) {
  const params = {};
  const kartFuarId = fuarId || aktifFuarId;
  if (suzgec.kimlik) params.kimlik = suzgec.kimlik;
  if (suzgec.kimlikTipi) params.kimlikTipi = suzgec.kimlikTipi;
  if (suzgec.eksikBelge) params.eksikBelge = suzgec.eksikBelge;
  if (suzgec.firma?.firmaId) params.firmaId = suzgec.firma.firmaId;
  if (fuarId && suzgec.kartDurumu) params.kartDurumu = suzgec.kartDurumu;
  if (fuarId && suzgec.kartSeviyesi) params.kartSeviyesi = suzgec.kartSeviyesi;
  if (fuarId && suzgec.firmaTipi) params.firmaTipi = suzgec.firmaTipi;
  if (suzgec.firmaBelgeTam) {
    params.firmaBelgeTam = suzgec.firmaBelgeTam;
    if (suzgec.firmaBelgeAktifFuar && !fuarId && aktifFuarId) params.firmaBelgeAktifFuar = true;
  }
  if (kartFuarId && suzgec.kartBasim) params.kartBasim = suzgec.kartBasim;
  if (kartFuarId && suzgec.kkd && suzgec.kartBasim !== KART_BASILDI_KKD_UYGUN) params.kkd = suzgec.kkd;
  const belge = suzgec.belge || {};
  // axios dizileri `belgeVar[]=` diye yollar; uç virgüllü listeyi de, tekrarlı parametreyi de kabul ediyor.
  Object.entries(BELGE_DURUM_PARAMETRESI).forEach(([durum, param]) => {
    const anahtarlar = Object.keys(belge).filter((k) => belge[k] === durum);
    if (anahtarlar.length) params[param] = anahtarlar.join(',');
  });
  return params;
}

/**
 * Açık süzgeç sayısı (rozet için). Fuar gerektirenler fuarsızken sayılmaz. "Aktif fuara göre" ayrı bir
 * süzgeç değil, firmaBelgeTam'ın kipi: ayrıca sayılmaz.
 */
export function aktifSuzgecSayisi(suzgec = BOS_SUZGEC, { fuarId = null, aktifFuarId = null } = {}) {
  const params = suzgeciParametreye(suzgec, { fuarId, aktifFuarId });
  const belgeSayisi = Object.values(suzgec.belge || {}).filter(Boolean).length;
  const belgeParamlari = Object.values(BELGE_DURUM_PARAMETRESI);
  return Object.keys(params)
    .filter((k) => !belgeParamlari.includes(k) && k !== 'firmaBelgeAktifFuar').length + belgeSayisi;
}

// ── Liste durumu ↔ URL (#260 / #262) ─────────────────────────────────────────
// Kişi Listesi'nin görünümü, araması, süzgeçleri ve sayfası adres çubuğunda yaşar: detaydan
// "Kişi Listesine Dön" ya da tarayıcı geri tuşu sayfayı yeniden kurunca hiçbiri kaybolmasın.
// Süzgeç adları uç parametreleriyle aynı (kimlik, eksikBelge, kartDurumu, firmaTipi, belgeVar/…);
// `firmaId` bilerek KULLANILMAZ — "Personel Ekle" ön dolumu (?yeni=1&firmaId=) onu zaten kullanıyor.

const LISTE_URL_ANAHTARLARI = [
  // `tumFuarlar` (#478: onay kutusu kalktı) yalnızca ESKİ bağlantılar silinsin diye kalıyor: okunmaz, bir sonraki yazımda düşer.
  'gorunum', 'q', 'sayfa', 'tumFuarlar', 'firma', 'firmaAd',
  'kimlik', 'kimlikTipi', 'eksikBelge', 'kartDurumu', 'kartSeviyesi', 'firmaTipi',
  'firmaBelgeTam', 'firmaBelgeAktifFuar', 'kartBasim', 'kkd', // #359/#373: uçtaki adlarla aynı
  ...Object.values(BELGE_DURUM_PARAMETRESI),
];

const KIMLIK_DEGERLERI = ['dogrulandi', 'dogrulanmadi', 'alanda']; // #664: alanda = durum 3 (dogrulanmadi 3'leri de içerir)
const KIMLIK_TIPI_DEGERLERI = KIMLIK_TIPI_SECENEKLERI.map((o) => o.value);
const KART_DURUMU_DEGERLERI = ['KART_ALABILIR', 'EVRAK_TAMAM_KART_ALAMAZ', 'EVRAK_EKSIK'];
const KART_SEVIYESI_DEGERLERI = KART_SEVIYESI_SECENEKLERI.map((o) => o.value);
const KART_BASIM_DEGERLERI = KART_BASIM_SECENEKLERI.map((o) => o.value);
const KKD_DEGERLERI = KKD_SECENEKLERI.map((o) => o.value);

/**
 * Adres çubuğundan liste durumunu okur (`suzgeciParametreye`'nin tersi). Tanınmayan değerler sessizce
 * düşer; fuar gerektiren süzgeçler yalnızca fuar görünümünde alınır.
 * @returns {{ fuarGorunumu: boolean, arama: string, sayfa: number, suzgec: object }}
 */
export function listeDurumuUrldenOku(searchParams) {
  const fuarGorunumu = searchParams.get('gorunum') === 'fuar';
  const belge = {};
  Object.entries(BELGE_DURUM_PARAMETRESI).forEach(([durum, param]) => {
    (searchParams.get(param) || '').split(',').forEach((k) => {
      // İlk gelen kazanır: aynı anahtar iki listedeyse uç 400 verirdi.
      if (BELGE_ANAHTARLARI.includes(k) && !belge[k]) belge[k] = durum;
    });
  });
  const firmaId = Number(searchParams.get('firma')) || null;
  const kartDurumu = searchParams.get('kartDurumu');
  const firmaTipi = searchParams.get('firmaTipi');
  const eksikBelge = searchParams.get('eksikBelge');
  const kartSeviyesi = searchParams.get('kartSeviyesi');
  const firmaBelgeTamParam = searchParams.get('firmaBelgeTam');
  const firmaBelgeTam = firmaBelgeTamParam === 'true' || firmaBelgeTamParam === 'false' ? firmaBelgeTamParam : '';
  const kartBasimParam = searchParams.get('kartBasim');
  const kartBasim = KART_BASIM_DEGERLERI.includes(kartBasimParam) ? kartBasimParam : '';
  const kkdParam = searchParams.get('kkd');
  return {
    fuarGorunumu,
    arama: searchParams.get('q') || '',
    sayfa: Math.max(1, Number(searchParams.get('sayfa')) || 1),
    // WAS (until #478): firmaTumFuarlar: fuarGorunumu && searchParams.get('tumFuarlar') === '1',
    suzgec: {
      ...BOS_SUZGEC,
      kimlik: KIMLIK_DEGERLERI.includes(searchParams.get('kimlik')) ? searchParams.get('kimlik') : '',
      kimlikTipi: KIMLIK_TIPI_DEGERLERI.includes(searchParams.get('kimlikTipi')) ? searchParams.get('kimlikTipi') : '',
      eksikBelge: eksikBelge === 'true' || eksikBelge === 'false' ? eksikBelge : '',
      kartDurumu: fuarGorunumu && KART_DURUMU_DEGERLERI.includes(kartDurumu) ? kartDurumu : '',
      kartSeviyesi: fuarGorunumu && KART_SEVIYESI_DEGERLERI.includes(kartSeviyesi) ? kartSeviyesi : '',
      firmaTipi: fuarGorunumu && FIRMA_TIPI_SECENEKLERI.some((o) => o.value === firmaTipi) ? firmaTipi : '',
      firmaBelgeTam,
      // "Aktif fuara göre" yalnızca Tüm Kişiler'de ve firmaBelgeTam ile birlikte geçerli (fuarId'li istek 400 verir).
      firmaBelgeAktifFuar: !fuarGorunumu && Boolean(firmaBelgeTam) && searchParams.get('firmaBelgeAktifFuar') === 'true',
      kartBasim,
      // kartBasim=basildi_kkd_uygun ile kkd birlikte olmaz (uç 400): ikisi de varsa kartBasim kazanır.
      kkd: kartBasim !== KART_BASILDI_KKD_UYGUN && KKD_DEGERLERI.includes(kkdParam) ? kkdParam : '',
      firma: firmaId ? { firmaId, firmaName: searchParams.get('firmaAd') || `Firma #${firmaId}` } : null,
      belge,
    },
  };
}

/**
 * Liste durumunu mevcut sorgu dizesine yazar: önce kendi anahtarlarını siler, sonra yalnızca varsayılandan
 * farklı olanları sabit sırayla ekler (aynı durum → aynı dize; gereksiz geçmiş/yenileme olmasın).
 * `yeni`, `firmaId` gibi başka anahtarlara dokunmaz.
 */
export function listeDurumunuUrleYaz(searchParams, { fuarGorunumu, arama, sayfa, suzgec }) {
  const next = new URLSearchParams(searchParams);
  LISTE_URL_ANAHTARLARI.forEach((k) => next.delete(k));
  if (fuarGorunumu) next.set('gorunum', 'fuar');
  if (arama) next.set('q', arama);
  if (sayfa > 1) next.set('sayfa', String(sayfa));
  // WAS (until #478): if (fuarGorunumu && firmaTumFuarlar) next.set('tumFuarlar', '1');
  if (suzgec.firma?.firmaId) {
    next.set('firma', String(suzgec.firma.firmaId));
    if (suzgec.firma.firmaName) next.set('firmaAd', suzgec.firma.firmaName);
  }
  if (suzgec.kimlik) next.set('kimlik', suzgec.kimlik);
  if (suzgec.kimlikTipi) next.set('kimlikTipi', suzgec.kimlikTipi);
  if (suzgec.eksikBelge) next.set('eksikBelge', suzgec.eksikBelge);
  if (fuarGorunumu && suzgec.kartDurumu) next.set('kartDurumu', suzgec.kartDurumu);
  if (fuarGorunumu && suzgec.kartSeviyesi) next.set('kartSeviyesi', suzgec.kartSeviyesi);
  if (fuarGorunumu && suzgec.firmaTipi) next.set('firmaTipi', suzgec.firmaTipi);
  if (suzgec.firmaBelgeTam) {
    next.set('firmaBelgeTam', suzgec.firmaBelgeTam);
    if (!fuarGorunumu && suzgec.firmaBelgeAktifFuar) next.set('firmaBelgeAktifFuar', 'true');
  }
  // kartBasim / kkd üst çubuk fuarına da bağlı olabilir; bu işlev fuarı bilmez, durum olduğu gibi yazılır
  // (istekte fuar yoksa zaten gönderilmez — suzgeciParametreye).
  if (suzgec.kartBasim) next.set('kartBasim', suzgec.kartBasim);
  if (suzgec.kkd && suzgec.kartBasim !== KART_BASILDI_KKD_UYGUN) next.set('kkd', suzgec.kkd);
  Object.entries(BELGE_DURUM_PARAMETRESI).forEach(([durum, param]) => {
    const anahtarlar = BELGE_ANAHTARLARI.filter((k) => suzgec.belge?.[k] === durum);
    if (anahtarlar.length) next.set(param, anahtarlar.join(','));
  });
  return next;
}

// ── Mock ─────────────────────────────────────────────────────────────────────

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

const MOCK_ROWS = [
  { personId: 78293, name: 'BERİVAN', surname: 'KARAKOÇ', tcNoMasked: '14*******26', meslekName: 'Elektrikçi', kimlik: 1, firmalar: [{ firmaId: 49858, firmaName: 'Örnek Yapı A.Ş.' }, { firmaId: 19497, firmaName: 'FUARAY MİMARLIK' }] },
  { personId: 78055, name: 'ABDULLAH', surname: 'BARUT', tcNoMasked: '32*******44', meslekName: null, kimlik: 0, firmalar: [{ firmaId: 49858, firmaName: 'Örnek Yapı A.Ş.' }] },
  { personId: 78042, name: 'MEHMET', surname: 'YILMAZ', tcNoMasked: '11*******90', meslekName: 'Marangoz', kimlik: 2, firmalar: [{ firmaId: 48832, firmaName: 'DIGICOM' }] },
  { personId: 78100, name: 'AYŞE', surname: 'DEMİR', tcNoMasked: '45*******12', meslekName: 'Temizlik', kimlik: 1, firmalar: [{ firmaId: 48832, firmaName: 'DIGICOM' }] },
  { personId: 78211, name: 'HASAN', surname: 'ÇELİK', tcNoMasked: '78*******31', meslekName: 'Nakliye', kimlik: 0, firmalar: [] },
].map((p) => ({
  ...p,
  forbidden: false,
  owner: false,
  documents: {},
  statusCounts: { approved: 0, pending: 0, rejected: 0, missing: 0, yasiGecmis: 0 },
  kimlikDogrulama: {
    durum: p.kimlik,
    durumAdi: ['Doğrulanmadı', 'Onaylandı', 'Reddedildi'][p.kimlik],
    dogrulandi: p.kimlik === 1,
  },
}));

function parseError(error) {
  const message =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:kisiListService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

/**
 * Tek sayfa. `page` 1-tabanlı (sayfa bileşenleri böyle sayıyor), uca 0-tabanlı gider.
 * #478: uç hâlâ `firmaTumFuarlar=true` kabul eder (fuar + arama varken firma adı eşleşmesini başka fuarlara genişletir, #117) ama
 * hiçbir ekran göndermiyor; onay kutusu kalktı.
 * @returns {{ records, total, page, totalPages }}
 */
export async function getKisiListesi({
  fuarId = null, aktifFuarId = null, search = '', page = 1, size = 25, belgeler = false,
  suzgec = BOS_SUZGEC,
} = {}) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const term = search.trim().toLocaleLowerCase('tr');
    const rows = MOCK_ROWS.filter((r) => !term || `${r.name} ${r.surname}`.toLocaleLowerCase('tr').includes(term));
    const start = (page - 1) * size;
    return { records: rows.slice(start, start + size), total: rows.length, page, totalPages: Math.max(1, Math.ceil(rows.length / size)) };
  }
  const params = { page: page - 1, size: Math.min(size, MAX_SIZE), ...suzgeciParametreye(suzgec, { fuarId, aktifFuarId }) };
  if (fuarId) params.fuarId = fuarId;
  // Üst çubuk fuarı (#359): satırlara kartBasim / kkd taşır, kartBasim / kkd / "Aktif fuara göre" süzgeçlerinin fuarı olur.
  // Fuar görünümünde fuarId ile aynı olmalı (farklıysa uç 400 verir) — sayfa zaten ikisini aynı değerden besliyor.
  if (aktifFuarId) params.aktifFuarId = fuarId && fuarId !== aktifFuarId ? fuarId : aktifFuarId;
  if (search.trim()) params.search = search.trim();
  // WAS (until #478): if (fuarId && search.trim() && firmaTumFuarlar) params.firmaTumFuarlar = true;
  if (belgeler) params.belgeler = true;
  try {
    const res = await axios.get(`${BASE}/kisi-listesi`, { params, headers: getAuthHeader() });
    const d = res.data || {};
    return {
      records: d.records || [],
      total: d.total ?? 0,
      page: (d.page ?? 0) + 1,
      totalPages: d.totalPages ?? 1,
    };
  } catch (error) {
    parseError(error);
  }
}
